import "./data-access-api-key.scss";

import FolderIcon from "assets/icons/common/FolderIcon";
import FolderOpenIcon from "assets/icons/common/FolderOpenIcon";
import SearchIcon from "assets/icons/common/SearchIcon";

import React from "react";
import {cx} from "emotion";

import {SearchInput} from "@common/form/search-input/search-input";
import {cs} from "@common/react/chain-services";
import {consumeContext} from "@common/react/context";
import {keyed} from "@common/react/keyed";
import {Load2} from "@common/react/load2";
import {UseState} from "@common/react/use-state";
import {createDateFormatter} from "@common/ui-components/charts/common/formatters/date-formatter";
import {NewFolderIcon, PlusIcon} from "@common/ui-components/icons/global-icons";
import {LoadingIndicator} from "@common/ui-components/loading-indicator/loading-indicator";
import {BasicPanel} from "@common/ui-components/panel/panel";
import {isEmpty} from "@common/utils/collections";
import {ConfirmDialog} from "../../../../common/confirm-dialog/confirm-dialog";
import {PopupMenu} from "../../../../common/popup-menu/popup-menu";
import {RecursiveTable} from "../../../../common/recursive-table/recursive-table";
import {StaticTooltipService} from "../../../../common/tooltip3/static-tooltip-service";
import {CreateFolderDialog} from "../../collections/folder-cm/create-folder/create-folder-dialog";
import {buildFoldersTree} from "../../collections/folders-tree-structure";
import {settingKey} from "../settings-constants";
import {CannotCreateApiKeyDialog} from "./cannot-create-api-key-dialog";
import {ManageAccessKeyDialog} from "./manage-key/manage-access-key-dialog";
import {SettingRollKeyDialog} from "./roll-key/setting-roll-key-dialog";
import {ViewAccessApiKeyDialog} from "./view-access-api-key-dialog";
import {FolderCM} from "../../collections/folder-cm/folder-cm";
import {MoveToDialog} from "../../common/move-to-dialog/move-to-dialog";
import {getFolderDepthOrderList} from "../../collections/folder-cm/get-folder-depth-order-list";
import {SelectTreeFoldersItem} from "../commons/select-tree-folders-item/select-tree-folders-item";
import {chain} from "@common/utils/fs";
import {cascade} from "@common/utils/cascade";
import {isBlank} from "@common/utils/strings";
import {ButtonDropdown} from "../../../../data-source/common/button-dropdown/button-dropdown";
import {DeleteKeyDialog} from "../commons/delete-key-dialog";
import {MultiSelect} from "../../../common/multi-select";
import {noun} from "@common/utils/plural";
import {recentStorage} from "../../recent-section/recent-storage";

export const loadDataAccessApiKeyActions = ({next, accessApiKeyFolders, apiCollections}) =>
    cs(
        consumeContext("apis"),
        consumeContext("routing"),
        ["manageKeyDialog", (_, next) => ManageAccessKeyDialog({next, folders: accessApiKeyFolders.value})],
        ["viewApiKeyDialog", (_, next) => ViewAccessApiKeyDialog({next})],
        ["rollKeyDialog", (_, next) => SettingRollKeyDialog({$type: settingKey.DATA_ACCESS, next})],
        ["confirmDialog", ({}, next) => ConfirmDialog({next, title: "Delete Key?"})],
        [
            "viewKeyParameters",
            ({manageKeyDialog}, next) =>
                next(async (ak, editStep) => {
                    const resp = await manageKeyDialog.show({
                        $type: settingKey.DATA_ACCESS,
                        accessKey: ak,
                        collections: apiCollections,
                        editStep,
                    });

                    if (resp) {
                        const {accessKey} = resp;
                        accessApiKeyFolders.reload();
                    }
                }),
        ],
        [
            "onUpdateFolders",
            (_, next) =>
                next((newFolders) => {
                    const res = accessApiKeyFolders.onChange(newFolders);
                    return res;
                }),
        ],
        [
            "deleteAccessKeyDialog",
            ({onUpdateFolders}, next) =>
                DeleteKeyDialog({
                    entityName: "accessKey",
                    entityDisplayName: "Data Access API Key",
                    title: "Delete Data Access API Key",
                    onDone: onUpdateFolders,
                    reloadFolderOnDone: true,
                    next,
                }),
        ],
        [
            "akCM",
            ({routing, moveAccessKeyDialog, deleteAccessKeyDialog, viewApiKeyDialog, manageKeyDialog, confirmDialog, apis, viewKeyParameters, rollKeyDialog}, next) =>
                PopupMenu({
                    itemHeight: 48,
                    getCommands: ({ak}) => {
                        return [
                            {
                                label: "Move to",
                                onClick: () => moveAccessKeyDialog.show({params: ak}),
                            },
                            {
                                label: "Key Parameters",
                                onClick: () => viewKeyParameters(ak, 0),
                            },
                            {
                                label: "Collection Access",
                                onClick: async () => viewKeyParameters(ak, 1),
                            },
                            {
                                label: "Reveal Key",
                                onClick: () => viewApiKeyDialog.show({accessKey: ak}),
                            },
                            {
                                label: "Roll Key",
                                onClick: async () => {
                                    confirmDialog.show({
                                        confirmTitle: `Roll ${ak.name} Key`,
                                        submitBtn: "Roll Key",
                                        description: `The current key will be deleted immediately. Are you sure you want to proceed? You may reveal or share the key next.`,
                                        confirmClick: async () => {
                                            const newKey = await apis.accessKey.rollKey(ak.id);

                                            accessApiKeyFolders.change((oldFolders) =>
                                                oldFolders.map((f) => ({
                                                    ...f,
                                                    keys: cascade(f.keys, "[*]", (key) =>
                                                        key.id === ak.id
                                                            ? {
                                                                  ...ak,
                                                                  apiKey: newKey,
                                                              }
                                                            : key
                                                    ),
                                                }))
                                            );

                                            const resp = await rollKeyDialog.show({
                                                accessKey: {
                                                    ...ak,
                                                    apiKey: newKey,
                                                },
                                            });
                                            if (resp) {
                                                if (resp == "reveal")
                                                    viewApiKeyDialog.show({
                                                        accessKey: {
                                                            ...ak,
                                                            apiKey: newKey,
                                                        },
                                                    });
                                                else viewKeyParameters(ak, 2);
                                            }
                                        },
                                    });
                                },
                            },
                            {
                                label: "Share Key",
                                onClick: () => viewKeyParameters(ak, 2),
                            },
                            {
                                label: "Delete",
                                className: "delete-action",
                                onClick: async () =>
                                    deleteAccessKeyDialog.show({
                                        items: [ak],
                                    }),
                            },
                        ];
                    },
                    minWidth: 150,
                    next,
                }),
        ],
        [
            "moveAccessKeyDialog",
            ({onUpdateFolders}, next) =>
                MoveToDialog({
                    apiType: "accessKey",
                    folders: accessApiKeyFolders.value,
                    onDone: onUpdateFolders,
                    next,
                }),
        ],
        ({manageKeyDialog, viewApiKeyDialog, akCM, viewKeyParameters, moveAccessKeyDialog, deleteAccessKeyDialog}) =>
            next({
                manageKeyDialog,
                viewApiKeyDialog,
                akCM,
                viewKeyParameters,
                moveAccessKeyDialog,
                deleteAccessKeyDialog,
            })
    );

export const DataAccessApiKey = ({tab, collectionFolders, apiCollections, accessApiKeyFolders}) =>
    cs(
        keyed("data-access-api"),
        consumeContext("auth"),
        consumeContext("authEndpoint"),
        consumeContext("apis"),
        consumeContext("routing"),
        ["searchText", (_, next) => UseState({initValue: "", next})],
        ["selectedCollections", (_, next) => UseState({initValue: [], next})],
        ["endpointAuthConfig", ({authEndpoint}, next) => next(authEndpoint.value.find((t) => t.$type === "EndpointAuthConfig") || {})],
        [
            "defaultUserValues",
            ({endpointAuthConfig}, next) => {
                let ret = {};
                if (endpointAuthConfig) {
                    for (let key in endpointAuthConfig.resultMapping) {
                        const mappingType = endpointAuthConfig.resultMapping[key].mappingType;
                        ret[key] = {
                            $type: mappingType == "SingleValue" ? "UserStringValue" : "UserArrayValue",
                            ...(mappingType == "SingleValue"
                                ? {value: ""}
                                : {
                                      values: [
                                          {
                                              $type: "UserStringValue",
                                              value: "",
                                          },
                                      ],
                                  }),
                        };
                    }
                    return next(ret);
                }

                return next(null);
            },
        ],
        [
            "modifyUserValues",
            ({endpointAuthConfig}, next) =>
                next((userValues) => {
                    let ret = {};
                    for (let key in endpointAuthConfig?.resultMapping) {
                        const map = {
                            SingleValue: "UserStringValue",
                            ValueArray: "UserArrayValue",
                        };

                        const mappingType = map[endpointAuthConfig?.resultMapping[key].mappingType];
                        if (userValues[key] && userValues[key].$type == mappingType) ret[key] = userValues[key];
                        else {
                            ret[key] = {
                                $type: mappingType,
                                ...(mappingType == "UserStringValue"
                                    ? {value: ""}
                                    : {
                                          values: [
                                              {
                                                  $type: "UserStringValue",
                                                  value: "",
                                              },
                                          ],
                                      }),
                            };
                        }
                    }

                    return ret;
                }),
        ],
        ["warningDialog", (_, next) => CannotCreateApiKeyDialog({next})],
        ["folderCM", (_, next) => FolderCM({apiType: "accessKey", folders: accessApiKeyFolders, next})],
        [
            "createFolderDialog",
            (_, next) =>
                CreateFolderDialog({
                    apiType: "accessKey",
                    folders: accessApiKeyFolders.value,
                    onCreate: (newFolder) => accessApiKeyFolders.onChange([...(accessApiKeyFolders.value || []), newFolder]),
                    next,
                }),
        ],
        ["dataAccessApiKeyActions", (_, next) => loadDataAccessApiKeyActions({next, accessApiKeyFolders, apiCollections})],
        [
            "multiSelectCtrl",
            ({dataAccessApiKeyActions: {moveAccessKeyDialog, deleteAccessKeyDialog}}, next) =>
                MultiSelect({
                    next,
                    itemName: "Key",
                    resolveItemKey: (item) => `${item.type}_${item.id}`,
                    actions: [
                        {
                            label: "Move To",
                            isShow: () => true,
                            onClick: async (selectedItems) => {
                                const updateItems = Object.values(selectedItems);
                                return await moveAccessKeyDialog.show({
                                    isUpdateMany: true,
                                    label: `${updateItems.length} ${noun(updateItems.length, "Data Access API Key")}`,
                                    params: updateItems,
                                });
                            },
                        },
                        {
                            label: "Delete",
                            isShow: () => true,
                            onClick: async (selectedItems) => {
                                const updateItems = Object.values(selectedItems);
                                return await deleteAccessKeyDialog.show({
                                    items: updateItems,
                                });
                            },
                        },
                    ],
                }),
        ],
        ({
            dataAccessApiKeyActions: {manageKeyDialog, viewApiKeyDialog, akCM, viewKeyParameters},
            defaultUserValues,
            endpointAuthConfig,
            warningDialog,
            searchText,
            folders,
            folderCM,
            selectedCollections,
            createFolderDialog,
            auth,
            multiSelectCtrl,
            routing,
        }) => {
            const hasSelectedItems = multiSelectCtrl.numSelectedItems > 0;
            const isFolder = (item) => item?.hasOwnProperty("children") || item?.hasOwnProperty("keys");
            const filteredItems = (list) => {
                if (selectedCollections.value.length > 0) {
                    return list.map((l) => ({
                        ...l,
                        keys: l.keys.filter((k) => k.apiCollections.filter((c) => selectedCollections.value.findIndex((sc) => sc.id === c) !== -1).length > 0),
                    }));
                }
                return list;
            };

            const allowEditing = ["Full"].includes(auth.user.roles[0]);

            const addRecent = (item, path) => {
                recentStorage.add({
                    envId: routing.params?.envId,
                    type: "data-access-api-key",
                    item,
                    path,
                });
            };

            return (
                <BasicPanel
                    className="data-access-api-key-a23"
                    title={cs(
                        [
                            "staticTooltip",
                            (_, next) =>
                                StaticTooltipService({
                                    direction: "top",
                                    info: "These keys are used to access data outputs from this Verb environment only. System API Keys are available in the Account Management section.",
                                    next,
                                }),
                        ],
                        ({staticTooltip}) => (
                            <div className="key-title">
                                <div>Data Access API Key&nbsp;</div>

                                {staticTooltip.renderIcon({
                                    icon: <i className="material-icons">info</i>,
                                    className: "info-tooltip",
                                })}
                            </div>
                        )
                    )}
                    rightControl={
                        <>
                            {SearchInput({
                                placeholder: "Search Key Name",
                                state: searchText,
                                icon: SearchIcon({}),
                            })}

                            {apiCollections.value &&
                                collectionFolders &&
                                SelectTreeFoldersItem({
                                    childrenProp: "collections",
                                    inputProps: {
                                        placeholder: "Filter by Collection",
                                    },
                                    list: getFolderDepthOrderList(
                                        chain(collectionFolders, (_) =>
                                            _.map((f) => ({
                                                ...f,
                                                collections: apiCollections.value.filter((c) => c.folderID === f.id && c.id !== null),
                                            }))
                                        )
                                    ),
                                    displayValue: selectedCollections.value.length > 0 ? `${selectedCollections.value.length} selected` : null,
                                    valueToInputLabel: (f) => f.name,
                                    valueToLabel: (f) => f.name,
                                    valueItemToLabel: (f) => f.name,
                                    onChange: (item, selected) => selectedCollections.change((old) => (selected ? [item, ...old] : old.filter((o) => o.id !== item.id))),
                                    isSelected: (item) => selectedCollections.value.findIndex((s) => s.id === item.id) > -1,
                                    unSelectAll: () => selectedCollections.onChange([]),
                                    sortBy: (x, y) => (x.name.toLowerCase() < y.name.toLowerCase() ? -1 : 1),
                                })}

                            {allowEditing &&
                                ButtonDropdown({
                                    label: "New",
                                    list: [
                                        {
                                            value: 0,
                                            label: (
                                                <>
                                                    <PlusIcon fill="#919EB0" /> Create New Key
                                                </>
                                            ),
                                        },
                                        {
                                            value: 1,
                                            label: (
                                                <>
                                                    <NewFolderIcon fill="#919EB0" /> Create New Folder
                                                </>
                                            ),
                                        },
                                    ],
                                    valueToLabel: (item) => item.label,
                                    onChange: (item) => {
                                        const maps = {
                                            0: async () => {
                                                if (endpointAuthConfig && !isEmpty(endpointAuthConfig?.resultMapping)) {
                                                    const resp = await manageKeyDialog.show({
                                                        $type: settingKey.DATA_ACCESS,
                                                        accessKey: {
                                                            userValues: defaultUserValues,
                                                            apiCollections: [],
                                                        },
                                                        collections: apiCollections,
                                                    });
                                                    if (resp) {
                                                        const {accessKey, share} = resp;

                                                        accessApiKeyFolders.change((oldFolders) =>
                                                            oldFolders.map((f) =>
                                                                f.id === accessKey.folderID
                                                                    ? {
                                                                          ...f,
                                                                          keys: [...f.keys, accessKey],
                                                                      }
                                                                    : f
                                                            )
                                                        );

                                                        addRecent(
                                                            accessKey,
                                                            (accessApiKeyFolders.value ?? []).filter((f) => f.id === accessKey.folderID)
                                                        );

                                                        if (share) {
                                                            await viewKeyParameters(accessKey, 2);
                                                        }
                                                    }
                                                } else {
                                                    const resp = await warningDialog.show();
                                                    if (resp) tab.onChange("authentication");
                                                }
                                            },
                                            1: createFolderDialog.show,
                                        };

                                        return maps[item.value]();
                                    },
                                })}

                            {hasSelectedItems && multiSelectCtrl.render({style: {marginBottom: 0, marginLeft: 16}})}
                        </>
                    }
                >
                    {({height}) => {
                        return (
                            <div className="data-access-api-keys-wrapper">
                                {!apiCollections.value || !accessApiKeyFolders.value
                                    ? LoadingIndicator()
                                    : RecursiveTable({
                                          showNoResult: searchText.value?.length > 0,
                                          onClearSearch: () => searchText.onChange(""),
                                          maxHeight: height,
                                          getTrClass: (n) => multiSelectCtrl.isSelected(n) && "is-selected-tr",
                                          getChildTrClass: (n) => (isFolder(n) && multiSelectCtrl.isSelected(n) ? "is-selected-tr" : ""),

                                          structure: buildFoldersTree({
                                              itemsProp: "keys",
                                              folders: filteredItems(accessApiKeyFolders.value),
                                              keyword: searchText.value,
                                              hideEmptyFolder: selectedCollections.value.length > 0,
                                          }),
                                          isGroupNode: (n) => n?.keys,
                                          isAlwaysExpanded: () => !isBlank(searchText.value),
                                          onClickRow: (item, event, path) => {
                                              if (isFolder(item)) {
                                                  return;
                                              }

                                              event.preventDefault();
                                              event.stopPropagation();
                                              multiSelectCtrl.toggleSelect(item);
                                          },
                                          columns: [
                                              {
                                                  label: "Name",
                                                  indented: true,
                                                  format: (key, {expanding, path}) => {
                                                      if (key.keys) {
                                                          return (
                                                              <div
                                                                  className={cx("icon-name-34n", "folder", {
                                                                      expanding,
                                                                  })}
                                                              >
                                                                  {expanding ? FolderOpenIcon({}) : FolderIcon({})}
                                                                  {key.name}
                                                              </div>
                                                          );
                                                      }

                                                      return (
                                                          <div
                                                              className={allowEditing && "link"}
                                                              onClick={(e) => {
                                                                  e.preventDefault();
                                                                  e.stopPropagation();
                                                                  addRecent(key, path);
                                                                  allowEditing && viewKeyParameters(key, 0);
                                                              }}
                                                          >
                                                              {key.name}
                                                          </div>
                                                      );
                                                  },
                                              },
                                              {
                                                  label: "Key",
                                                  format: (key, {path}) =>
                                                      key.keys ? null : (
                                                          <div
                                                              className="api-key-item"
                                                              onClick={(e) => {
                                                                  e.preventDefault();
                                                                  e.stopPropagation();
                                                                  addRecent(key, path);
                                                                  viewApiKeyDialog.show({
                                                                      accessKey: key,
                                                                  });
                                                              }}
                                                          >
                                                              <span className="item">
                                                                  <b>.........................</b>
                                                                  <div className="tooltip">Reveal key</div>
                                                              </span>
                                                          </div>
                                                      ),
                                              },
                                              {
                                                  label: "Collections",
                                                  format: (key, {path}) =>
                                                      key.keys ? null : (
                                                          <div
                                                              className={allowEditing && "link"}
                                                              onClick={(e) => {
                                                                  e.preventDefault();
                                                                  e.stopPropagation();
                                                                  addRecent(key, path);
                                                                  allowEditing && viewKeyParameters(key, 1);
                                                              }}
                                                          >
                                                              {key.apiCollections?.length}
                                                          </div>
                                                      ),
                                              },
                                              {
                                                  label: "Key generated",
                                                  format: (key, {path}) =>
                                                      key.keys ? null : (
                                                          <div
                                                              className="api-key-item"
                                                              onClick={(e) => {
                                                                  e.preventDefault();
                                                                  e.stopPropagation();
                                                                  addRecent(key, path);
                                                                  viewKeyParameters(key);
                                                              }}
                                                          >
                                                              {createDateFormatter("MMM d, yyyy @ h:mm tt").format(new Date(key.created))}
                                                          </div>
                                                      ),
                                              },
                                              {
                                                  format: (key) => {
                                                      if (!allowEditing) {
                                                          return;
                                                      }
                                                      return (
                                                          <div className="cm">
                                                              {key.keys
                                                                  ? folderCM.render({
                                                                        params: key,
                                                                    })
                                                                  : akCM.render({
                                                                        params: {
                                                                            ak: key,
                                                                        },
                                                                        img: <i className="far fa-ellipsis-h" />,
                                                                    })}
                                                          </div>
                                                      );
                                                  },
                                                  className: "models-cm-35v",
                                                  shy: true,
                                                  alignRight: true,
                                              },
                                          ],
                                      })}
                            </div>
                        );
                    }}
                </BasicPanel>
            );
        }
    );
